import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, concatMap, debounceTime, filter, map, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { CustomerActions } from "./customer.actions";
import { CustomersActions } from "./customers/customers.actions";
import { ContactsActions } from "../contact/contacts/contacts.actions";
import { Store } from "@ngrx/store";
import { selectSelectedCompany } from "../../user/user.selectors";
import { selectCustomer } from "./customer.selectors";
import { AttioClient, CustomerClient, DeliveryAddressClient } from "src/api-service";
import { Router } from "@angular/router";
import { QuoteActions } from "../quote/quote.actions";
import { normalizeError } from "../../app-store-error.types";

@Injectable()
export class CustomerEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly customerClient = inject(CustomerClient);
    private readonly deliveryAddressClient = inject(DeliveryAddressClient);
    private readonly attioClient = inject(AttioClient);
    private readonly router = inject(Router);

    // Other Store triggers
    initializeCustomerTriggers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                QuoteActions.initializeQuoteSuccess,
                QuoteActions.duplicateQuoteSuccess,
                QuoteActions.changeCustomerSuccess,
            ),
            map(({ quote }) => CustomerActions.initializeCustomer({ customerCode: quote.customer.code })),
        ),
    );

    // Initialize Customer
    initializeCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.initializeCustomer),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ customerCode }, selectedCompany]) =>
                this.customerClient.get(customerCode, selectedCompany.id).pipe(
                    map(customer => CustomerActions.initializeCustomerSuccess({ customer })),
                    catchError(error => of(CustomerActions.initializeCustomerFailure({ error: normalizeError(error), toastKey: "CUSTOMER.TOAST.FETCH_ERROR" }))),
                ),
            ),
        ),
    );

    // Update Customer
    updateCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.updateCustomer),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ customerCode, request }, selectedCompany]) =>
                this.customerClient.patch(customerCode, request, selectedCompany.id).pipe(
                    map(customer => CustomerActions.updateCustomerSuccess({ customer, toastKey: "CUSTOMER.TOAST.UPDATE_SUCCESS" })),
                    catchError(error => of(CustomerActions.updateCustomerFailure({ error: normalizeError(error), toastKey: "CUSTOMER.TOAST.UPDATE_ERROR" }))),
                ),
            ),
        ),
    );

    // remove customer
    removeCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.removeCustomer),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ customerCode }, selectedCompany]) =>
                this.customerClient.remove(customerCode, selectedCompany.id).pipe(
                    mergeMap(() => {
                        const updateSuccessAction = CustomerActions.removeCustomerSuccess({ toastKey: "CUSTOMER.TOAST.REMOVE_SUCCESS" });
                        return [updateSuccessAction];
                    }),
                    catchError(error => {
                        return of(CustomerActions.removeCustomerFailure({ error: normalizeError(error), toastKey: "CUSTOMER.TOAST.REMOVE_ERROR" }));
                    }),
                ),
            ),
        ),
    );

    // create new customer
    createCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.createCustomer),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ customer, skipNavigation }, selectedCompany]) =>
                this.customerClient.create(selectedCompany.id, customer).pipe(
                    mergeMap(created => [
                        CustomerActions.createCustomerSuccess({ customerCode: created.code, skipNavigation }),
                        CustomersActions.invalidate(),
                    ]),
                    catchError(error => of(CustomerActions.createCustomerFailure({ error: normalizeError(error), toastKey: "CUSTOMER.TOAST.CREATE_ERROR" }))),
                ),
            ),
        ),
    );

    // ************************************************** SEARCH EFFECTS **************************************************
    searchCustomers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.searchCustomers),
            debounceTime(300),
            filter(action => action.searchValue.length >= 2),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([action, selectedCompany]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(CustomerActions.searchCustomersFailure({ error: { message: "No company selected" }, toastKey: "CUSTOMER.TOAST.SEARCH_ERROR" }));
                }

                return this.customerClient.getFiltered(companyId, 1, 100, action.searchValue).pipe(
                    mergeMap(response => [CustomerActions.searchCustomersSuccess({ results: response.data ?? [] })]),
                    catchError(error => of(CustomerActions.searchCustomersFailure({ error: normalizeError(error), toastKey: "CUSTOMER.TOAST.SEARCH_ERROR" }))),
                );
            }),
        ),
    );

    // ************************************************** NAVIGATION EFFECTS **************************************************
    // Navigate to customer page (skipped when skipNavigation is true, e.g. subscription flow)
    navigateToCustomer$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CustomerActions.createCustomerSuccess),
                withLatestFrom(this.store.select(selectSelectedCompany)),
                tap(([{ customerCode, skipNavigation }, selectedCompany]) => {
                    if (!skipNavigation) {
                        this.router.navigate([selectedCompany.id, "customer", customerCode]);
                    }
                }),
            ),
        { dispatch: false },
    );

    // ************************************************** DELIVERY ADDRESS EFFECTS **************************************************
    // Create delivery address (requires an existing quote to use the API)
    createDeliveryAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.createDeliveryAddress),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCustomer),
            ),
            switchMap(([{ address }, selectedCompany, customer]) => {
                // Find a quote to use for creating the delivery address
                const quote = customer?.quotes?.[0];
                if (!quote?.id) {
                    return of(CustomerActions.createDeliveryAddressFailure({
                        error: { message: "No quote available" },
                        toastKey: "CUSTOMER.TOAST.DELIVERY_ADDRESS_NO_QUOTE",
                    }));
                }

                return this.deliveryAddressClient.create(quote.id, address, selectedCompany.id).pipe(
                    map(newAddress => CustomerActions.createDeliveryAddressSuccess({
                        address: newAddress,
                        toastKey: "CUSTOMER.TOAST.DELIVERY_ADDRESS_CREATED",
                    })),
                    catchError(error => of(CustomerActions.createDeliveryAddressFailure({
                        error: normalizeError(error),
                        toastKey: "CUSTOMER.TOAST.DELIVERY_ADDRESS_CREATE_ERROR",
                    }))),
                );
            }),
        ),
    );

    // ************************************************** ATTIO SYNC EFFECTS **************************************************
    syncFromAttio$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.syncFromAttio),
            switchMap(({ customerCode }) =>
                this.attioClient.syncCustomer(customerCode).pipe(
                    mergeMap(() => [
                        CustomerActions.syncFromAttioSuccess({ customerCode, toastKey: "CUSTOMER.TOAST.SYNC_SUCCESS" }),
                        CustomerActions.initializeCustomer({ customerCode }),
                        CustomersActions.invalidate(),
                        ContactsActions.invalidate(),
                    ]),
                    catchError(error => of(CustomerActions.syncFromAttioFailure({
                        error: normalizeError(error),
                        toastKey: "CUSTOMER.TOAST.SYNC_ERROR",
                    }))),
                ),
            ),
        ),
    );

}
